import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CloudSun, Loader2, Sparkles, Play, Save, Sun, CloudRain, Snowflake, Cloud, Moon, Sunset, Sunrise, RefreshCw, Wind, Waves, Timer } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

const SKIES = [
  { key: "day", label: "Clear Day", grad: "linear-gradient(#7ec8f0,#cdefff)", icon: Sun },
  { key: "golden", label: "Golden Hour", grad: "linear-gradient(#ffb347,#ffe6b8)", icon: Sunrise },
  { key: "sunset", label: "Sunset", grad: "linear-gradient(#ff7e5f,#feb47b)", icon: Sunset },
  { key: "dusk", label: "Dusk", grad: "linear-gradient(#6a5a9e,#c79bde)", icon: Moon },
  { key: "night", label: "Night", grad: "linear-gradient(#0b1430,#233a70)", icon: Moon },
];
const WEATHERS = [
  { key: "clear", label: "Clear", icon: Sun },
  { key: "rain", label: "Rain", icon: CloudRain },
  { key: "snow", label: "Snow", icon: Snowflake },
  { key: "sandstorm", label: "Sandstorm", icon: Wind },
];
const FOGS = [
  { key: "light", label: "Light" },
  { key: "medium", label: "Medium" },
  { key: "heavy", label: "Heavy" },
];

export default function WeatherStudio() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sky, setSky] = useState("day");
  const [weather, setWeather] = useState("clear");
  const [fog, setFog] = useState("medium");
  const [dayNight, setDayNight] = useState(false);
  const [waterEnabled, setWaterEnabled] = useState(false);
  const [waterColor, setWaterColor] = useState("#2a8fd6");
  const [waterLevel, setWaterLevel] = useState(1.5);
  const [dynamic, setDynamic] = useState(false);
  const [dynamicPeriod, setDynamicPeriod] = useState(45);
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/weather/${gameId}`);
      setSky(data.sky || "day"); setWeather(data.weather || "clear"); setFog(data.fog || "medium"); setDayNight(!!data.day_night);
      setWaterEnabled(!!data.water_enabled); setWaterColor(data.water_color || "#2a8fd6"); setWaterLevel(data.water_level ?? 1.5);
      setDynamic(!!data.dynamic); setDynamicPeriod(data.dynamic_period || 45);
    } catch (e) { toast.error("Couldn't open the Weather Engine."); }
    finally { setLoading(false); }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const mark = (fn) => (v) => { fn(v); setDirty(true); };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/weather/${gameId}/suggest`, {}, { timeout: 60000 });
      setSky(data.sky); setWeather(data.weather); setFog(data.fog); setDayNight(!!data.day_night);
      setNote(data.note || ""); setDirty(true);
      toast.success("🌤️ AI picked an atmosphere — tweak it, then Save.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't suggest weather.");
    } finally { setSuggesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/weather/${gameId}`, { sky, weather, fog, day_night: dayNight,
        water_enabled: waterEnabled, water_color: waterColor, water_level: Number(waterLevel),
        dynamic, dynamic_period: Number(dynamicPeriod) });
      setDirty(false);
      toast.success("✅ Weather saved — it'll show next time you play!");
    } catch (e) { toast.error("Couldn't save weather."); }
    finally { setSaving(false); }
  };

  const skyGrad = SKIES.find((s) => s.key === sky)?.grad || SKIES[0].grad;

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="weather-studio">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="weather-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2 truncate"><CloudSun className="w-6 h-6 text-sky-500" /> Weather & Sky</h1>
            <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">Dynamic weather + time-of-day skies for your 3D world</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate(`/app/arcade/${gameId}`)} data-testid="weather-play-btn"><Play className="w-4 h-4" /> Play</Button>
          <Button size="sm" className="rounded-full" onClick={save} disabled={saving || !dirty} data-testid="weather-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {dirty ? "Save" : "Saved"}</Button>
        </div>
      </div>

      {/* Live sky preview */}
      <div className="relative rounded-2xl overflow-hidden mb-5 border border-[hsl(var(--border))]" data-testid="weather-preview" style={{ height: 160 }}>
        <div className="absolute inset-0 transition-all duration-500" style={{ background: skyGrad }} />
        {weather === "rain" && <div className="absolute inset-0 opacity-60" style={{ backgroundImage: "repeating-linear-gradient(105deg, rgba(180,210,255,.5) 0 1px, transparent 1px 7px)" }} />}
        {weather === "snow" && <div className="absolute inset-0 opacity-70" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,.9) 1.4px, transparent 1.5px)", backgroundSize: "22px 22px" }} />}
        {fog !== "light" && <div className="absolute inset-x-0 bottom-0 h-2/3" style={{ background: `linear-gradient(transparent, rgba(220,225,235,${fog === "heavy" ? 0.75 : 0.4}))` }} />}
        <div className="absolute bottom-3 left-4 text-white drop-shadow font-display font-bold text-sm">
          {SKIES.find((s) => s.key === sky)?.label} · {WEATHERS.find((w) => w.key === weather)?.label}{dayNight ? " · day↔night cycle" : ""}
        </div>
      </div>

      {/* AI suggest */}
      <div className="rounded-2xl border border-sky-500/40 bg-gradient-to-br from-sky-500/10 to-indigo-500/5 p-4 mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-semibold flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-sky-500" /> Let AI read your world</p>
          {note ? <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{note}</p> : <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Pick a fitting sky + weather from your biome & story.</p>}
        </div>
        <Button variant="outline" size="sm" className="rounded-full shrink-0" onClick={suggest} disabled={suggesting} data-testid="weather-suggest-btn">{suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Suggest atmosphere</Button>
      </div>

      {/* Sky */}
      <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Time of day</p>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-5" data-testid="weather-sky-options">
        {SKIES.map((s) => {
          const on = sky === s.key; const Icon = s.icon;
          return (
            <button key={s.key} onClick={() => mark(setSky)(s.key)} data-testid={`weather-sky-${s.key}`}
              className={`rounded-xl border p-2 text-center transition-all ${on ? "border-sky-500 ring-2 ring-sky-500/40" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
              <div className="h-10 rounded-lg mb-1.5" style={{ background: s.grad }} />
              <div className="flex items-center justify-center gap-1 text-[11px] font-semibold"><Icon className="w-3 h-3" /> {s.label}</div>
            </button>
          );
        })}
      </div>

      {/* Weather */}
      <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Weather</p>
      <div className="grid grid-cols-3 gap-2 mb-5" data-testid="weather-weather-options">
        {WEATHERS.map((w) => {
          const on = weather === w.key; const Icon = w.icon;
          return (
            <button key={w.key} onClick={() => mark(setWeather)(w.key)} data-testid={`weather-weather-${w.key}`}
              className={`inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${on ? "border-sky-500 bg-sky-500/10 text-sky-500" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
              <Icon className="w-4 h-4" /> {w.label}
            </button>
          );
        })}
      </div>

      {/* Fog + day-night */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Fog / haze</p>
          <div className="grid grid-cols-3 gap-2" data-testid="weather-fog-options">
            {FOGS.map((f) => {
              const on = fog === f.key;
              return <button key={f.key} onClick={() => mark(setFog)(f.key)} data-testid={`weather-fog-${f.key}`}
                className={`inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 py-3 text-sm font-semibold transition-colors ${on ? "border-sky-500 bg-sky-500/10 text-sky-500" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}><Cloud className="w-4 h-4" /> {f.label}</button>;
            })}
          </div>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Day–night cycle</p>
          <button onClick={() => mark(setDayNight)(!dayNight)} data-testid="weather-daynight-toggle"
            className={`w-full inline-flex items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${dayNight ? "border-sky-500 bg-sky-500/10 text-sky-500" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
            <span className="flex items-center gap-1.5"><Sun className="w-4 h-4" /> Sun rises & sets over time</span>
            <span className={`w-10 h-5 rounded-full relative transition-colors ${dayNight ? "bg-sky-500" : "bg-[hsl(var(--muted))]"}`}><span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${dayNight ? "left-5" : "left-0.5"}`} /></span>
          </button>
        </div>
      </div>

      {/* Dynamic weather */}
      <div className="mt-5">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Dynamic weather</p>
        <button onClick={() => mark(setDynamic)(!dynamic)} data-testid="weather-dynamic-toggle"
          className={`w-full inline-flex items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${dynamic ? "border-sky-500 bg-sky-500/10 text-sky-500" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
          <span className="flex items-center gap-1.5"><Timer className="w-4 h-4" /> Weather shifts over time (clear → storms → clear)</span>
          <span className={`w-10 h-5 rounded-full relative transition-colors ${dynamic ? "bg-sky-500" : "bg-[hsl(var(--muted))]"}`}><span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${dynamic ? "left-5" : "left-0.5"}`} /></span>
        </button>
        {dynamic && (
          <div className="mt-2 flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))]" data-testid="weather-dynamic-period">
            <span>Change every</span>
            <input type="range" min="15" max="180" step="5" value={dynamicPeriod} onChange={(e) => mark(setDynamicPeriod)(e.target.value)} className="flex-1 accent-sky-500" data-testid="weather-dynamic-slider" />
            <span className="font-mono text-[hsl(var(--foreground))] w-12 text-right">{dynamicPeriod}s</span>
          </div>
        )}
      </div>

      {/* Water */}
      <div className="mt-5">
        <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Water</p>
        <button onClick={() => mark(setWaterEnabled)(!waterEnabled)} data-testid="weather-water-toggle"
          className={`w-full inline-flex items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${waterEnabled ? "border-sky-500 bg-sky-500/10 text-sky-500" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>
          <span className="flex items-center gap-1.5"><Waves className="w-4 h-4" /> Add a swimmable body of water</span>
          <span className={`w-10 h-5 rounded-full relative transition-colors ${waterEnabled ? "bg-sky-500" : "bg-[hsl(var(--muted))]"}`}><span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${waterEnabled ? "left-5" : "left-0.5"}`} /></span>
        </button>
        {waterEnabled && (
          <div className="mt-2 grid sm:grid-cols-2 gap-3" data-testid="weather-water-controls">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[hsl(var(--muted-foreground))]">Colour</span>
              <input type="color" value={waterColor} onChange={(e) => mark(setWaterColor)(e.target.value)} data-testid="weather-water-color" className="w-9 h-9 rounded-md border border-[hsl(var(--border))] bg-transparent cursor-pointer" />
              <span className="font-mono text-[hsl(var(--muted-foreground))]">{waterColor}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[hsl(var(--muted-foreground))] whitespace-nowrap">Level</span>
              <input type="range" min="0" max="6" step="0.5" value={waterLevel} onChange={(e) => mark(setWaterLevel)(e.target.value)} className="flex-1 accent-sky-500" data-testid="weather-water-level" />
              <span className="font-mono text-[hsl(var(--foreground))] w-10 text-right">{Number(waterLevel).toFixed(1)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
